import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CardContent,
  useTheme,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { message } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { nativeDateFieldProps } from "../Components/CalendarDatePicker";

const UpdateExpenses = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = React.useState({
    bill_id: "",
    description: "",
    amount: "",
    branch_id: "",
    date: new Date().toISOString().split("T")[0],
  });
  const token = localStorage.getItem("token");
  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";
  const [userRole, setUserRole] = React.useState("");
  const [userBranch, setUserBranch] = React.useState("");

  React.useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role);
        setUserBranch(user.branch_id);

        if (user.role !== "superadmin") {
          setFormData((prev) => ({
            ...prev,
            branch_id: user.branch_id?.toString() || "",
          }));
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://api.clever-school.com/api/expenses/${id}`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (isMounted) {
          const expenseData = response.data;
          setFormData((prev) => ({
            ...prev,
            bill_id: expenseData.bill_id || "",
            description: expenseData.description || "",
            amount: expenseData.amount || "",
            date: expenseData.date || new Date().toISOString().split("T")[0],
            branch_id:
              expenseData.branch_id?.toString() || userBranch?.toString() || "",
          }));
        }
      } catch (error) {
        console.error("Error fetching expense data:", error);
        message.error(t("error.fetchingData"));
      }
    };

    if (id) fetchData();
    return () => {
      isMounted = false;
    };
  }, [id, t, token, userBranch]);

  const handleChange = (name) => (e) => {
    setFormData({ ...formData, [name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `https://api.clever-school.com/api/expenses/${id}`,
        {
          ...formData,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(t("expenseUpdated"));
      navigate("/GetExpenses");
    } catch (error) {
      console.error("Update error:", error);
      message.error(t("expenseUpdateFailed"));
    }
  };

  const formFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: "none",
        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const formSelect = {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": {
      border: "none",
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
          padding: theme.spacing(2),
          margin: "20px auto",
        },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
          },
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("updateExpenses")}
          </Typography>
          <Grid container spacing={3}>
            {/* Bill ID Field */}
            <Grid item xs={12} md={6}>
              <CardContent sx={{ py: 2, px: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("billId")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.bill_id}
                  onChange={handleChange("bill_id")}
                  placeholder={t("enterBillId")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            {/* Date Field */}
            <Grid item xs={12} md={6}>
              <CardContent sx={{ py: 2, px: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("date")}
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formData.date}
                  onChange={handleChange("date")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...nativeDateFieldProps()}
                />
              </CardContent>
            </Grid>

            {/* Amount Field */}
            <Grid item xs={12} md={6}>
              <CardContent sx={{ py: 2, px: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("amount")}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={formData.amount}
                  onChange={handleChange("amount")}
                  placeholder={t("enterAmount")}
                  variant="outlined"
                  required
                  sx={formFieldStyles}
                />
              </CardContent>
            </Grid>

            {/* Branch Field - Only visible for superadmin */}
            {userRole === "superadmin" && (
              <Grid item xs={12} md={6}>
                <CardContent sx={{ py: 2, px: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("branch")}
                  </Typography>
                  <Select
                    fullWidth
                    value={formData.branch_id || ""}
                    onChange={handleChange("branch_id")}
                    variant="outlined"
                    displayEmpty
                    required
                    sx={formSelect}
                  >
                    <MenuItem value="" disabled>
                      {t("selectBranch")}
                    </MenuItem>
                    <MenuItem value="1">{t("branch1.1")}</MenuItem>
                    <MenuItem value="2">{t("branch1.2")}</MenuItem>
                    <MenuItem value="3">{t("branch1.3")}</MenuItem>
                  </Select>
                </CardContent>
              </Grid>
            )}

            {/* Description Field */}
            <Grid item xs={12}>
              <CardContent sx={{ py: 2, px: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  {t("description")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.description}
                  onChange={handleChange("description")}
                  placeholder={t("enterExpenseDescription")}
                  variant="outlined"
                  multiline
                  sx={formFieldStyles}
                  rows={4}
                />
              </CardContent>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/GetExpenses")}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.error.light,
                      borderColor: theme.palette.error.main,
                    },
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    px: 6,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: theme.palette.success.main,
                    "&:hover": {
                      backgroundColor: theme.palette.success.dark,
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  {t("update")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default UpdateExpenses;
